/**
 * A schedule: a month, with each row on the day its date column puts it.
 *
 * The one view type here that is not a row window. Its rows are days -- each
 * with its own page and its own count, and no global ordering between them --
 * so it is a keyed answer per month instead. See `queries.ts`.
 *
 * Read-only for now: a card shows a row and opens it. Dragging one to another
 * day is a write to the date column and arrives with the rest of Phase B's
 * editing pass.
 */
import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { rowColors } from '../decorations/colors'
import { getFieldTimezone, type DateFieldLike } from '../fields/dates'
import { useFieldContext } from '../fields/ui/context'
import { CellDisplay } from '../fields/ui/read'
import { RowModal } from '../row/row-modal'
import { useDetachedRow } from '../row/use-detached-row'
import type { RowSearch } from '../rows/rowQuery'
import { hiddenFields, visibleFields, type FieldPlacement } from '../views/field-order'
import {
  WEEKDAYS,
  addMonths,
  dayKey,
  monthGrid,
  monthLabel,
  monthWindow,
  sameMonth,
  startOfMonth,
} from './calendar'
import { useScheduleRows } from './queries'

/** How many rows a day cell draws before it says how many more there are. */
const PER_DAY = 3

export interface ScheduleViewProps {
  view: ViewOf<'schedule'>
  fields: readonly Field[]
  fieldOptions: Record<string, FieldPlacement>
  search: RowSearch
  openRowId: number | null
  onOpenRow: (rowId: number | null) => void
  /** The month first shown. A parameter so a test does not depend on today. */
  initialMonth?: Date
}

export function ScheduleView({
  view,
  fields,
  fieldOptions,
  search,
  openRowId,
  onOpenRow,
  initialMonth,
}: ScheduleViewProps) {
  const { t } = useTranslation()
  const context = useFieldContext(fields[0]?.workspace_id)
  const [month, setMonth] = useState(() => startOfMonth(initialMonth ?? new Date()))

  const dateField = fields.find((field) => field.id === view.date_field) ?? null
  // The zone the server is asked to bucket in, and the zone the window's
  // instants are computed in -- the same one the cell would be rendered in, so
  // a row appears on the day its reader sees on it.
  const timezone =
    dateField === null ? null : getFieldTimezone(dateField as DateFieldLike)
  const window = monthWindow(month, timezone)

  const days = useScheduleRows(
    view.table_id,
    view.id,
    { ...window, timezone: timezone ?? 'UTC' },
    search,
  )

  // A row opened from a cell is always one the month's answer is holding, but a
  // link somebody was sent may name one on another month entirely -- so the
  // dialog reads it through a window of its own, the way the grid does for a row
  // outside its buffer.
  const detached = useDetachedRow({
    tableId: view.table_id,
    viewId: view.id,
    rowId: openRowId,
    fields,
    view,
  })

  if (dateField === null) {
    return (
      <Empty className="p-6">
        <EmptyHeader>
          <EmptyTitle>{t('schedule.unconfiguredTitle')}</EmptyTitle>
          <EmptyDescription>{t('schedule.unconfiguredDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  const shown = visibleFields(fields, fieldOptions)
  const title = shown[0] ?? null
  const grid = monthGrid(month)
  const today = dayKey(new Date(), timezone)
  const decorations = view.decorations ?? []

  return (
    <div className="flex h-full min-h-0 flex-col p-3">
      <div className="flex shrink-0 items-center gap-2 pb-2">
        <Button
          variant="outline"
          size="icon"
          aria-label={t('schedule.previousMonth')}
          onClick={() => {
            setMonth(addMonths(month, -1))
          }}
        >
          <ChevronLeft />
        </Button>
        <div className="text-sm font-medium">{monthLabel(month)}</div>
        <Button
          variant="outline"
          size="icon"
          aria-label={t('schedule.nextMonth')}
          onClick={() => {
            setMonth(addMonths(month, 1))
          }}
        >
          <ChevronRight />
        </Button>
        {days.isFetching && <Skeleton className="h-4 w-16" />}
      </div>

      <div
        role="grid"
        aria-label={t('schedule.label')}
        className="grid min-h-0 flex-1 grid-cols-7 grid-rows-[auto_repeat(6,minmax(0,1fr))] gap-px overflow-auto"
      >
        {WEEKDAYS.map((weekday) => (
          <div
            key={weekday}
            role="columnheader"
            className="text-muted-foreground p-1 text-center text-xs"
          >
            {weekday}
          </div>
        ))}

        {grid.map((day) => {
          const key = dayKey(day, null)
          const held = days.data?.[key]
          const rows = held?.results ?? []
          const shownRows = rows.slice(0, PER_DAY)
          // How many the day holds that the cell is not showing. Both halves
          // read from the same two numbers, so the line cannot appear saying a
          // figure that disagrees with why it appeared -- which it did when the
          // condition counted what arrived and the number counted the day.
          const notShown = (held?.count ?? 0) - shownRows.length
          const inMonth = sameMonth(day, month)
          return (
            <div
              key={key}
              role="gridcell"
              data-testid={`day-${key}`}
              aria-label={key}
              className={cn(
                'min-h-16 space-y-0.5 overflow-hidden rounded border p-1 text-xs',
                inMonth ? 'bg-card' : 'bg-muted/30 text-muted-foreground',
              )}
            >
              <div
                className={cn(
                  'tabular-nums',
                  key === today && 'bg-primary text-primary-foreground rounded px-1',
                )}
              >
                {day.getUTCDate()}
              </div>
              {shownRows.map((row) => {
                const colors = rowColors(decorations, row, fields, context)
                return (
                  <button
                    key={row.id}
                    type="button"
                    data-testid={`event-${row.id}`}
                    onClick={() => {
                      onOpenRow(row.id)
                    }}
                    className="bg-card hover:border-ring/40 focus-visible:ring-ring block w-full truncate rounded border px-1 py-0.5 text-left focus-visible:ring-2 focus-visible:outline-none"
                    style={{
                      ...(colors.background === null
                        ? {}
                        : { backgroundColor: colors.background }),
                      ...(colors.leftBorder === null
                        ? {}
                        : { borderLeft: `3px solid ${colors.leftBorder}` }),
                    }}
                  >
                    {title === null ? (
                      `#${row.id}`
                    ) : (
                      <CellDisplay field={title} row={row} context={context} />
                    )}
                  </button>
                )
              })}
              {/* The whole day's count rather than what arrived, so this is a
                  number rather than "more" -- and it stays right when the page
                  size changes. */}
              {notShown > 0 ? (
                <div className="text-muted-foreground px-1">
                  {t('schedule.more', { count: notShown })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>

      {openRowId !== null && (
        <RowModal
          member={detached.member}
          rowId={openRowId}
          row={detached.row}
          missing={detached.missing}
          fields={shown}
          hidden={hiddenFields(fields, fieldOptions)}
          context={context}
          // A schedule has no order to step through: two rows on the same day
          // are not one before the other, and the days are a grid rather than a
          // sequence. Offering the buttons would be offering a direction that
          // does not exist.
          steps={{ previous: false, next: false }}
          onStep={() => {
            /* unreachable: both steps are refused above. */
          }}
          onClose={() => {
            onOpenRow(null)
          }}
        />
      )}
    </div>
  )
}
